import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';

import {
  BenefitSettingsForm,
  ICreateBenefitGroupsBatchRequest
} from '@epi-models/containers/ManageBenefitGroups';
import {
  FundsLoadingTypes,
  LimitType,
  LUNCH
} from '@epi-constants/benefitTypes';
import { useBenefitAmounts } from '@epi-containers/ManageBenefitGroups/hooks';
import { IActivateBenefitGroups } from '@epi-models/forms/ActivateBenefitGroups';

export const ActivateBenefitGroupsForm = ({
  selectedBenefits,
  mutationTrigger,
  children
}: IActivateBenefitGroups) => {
  const benefitMaxAmounts = useBenefitAmounts();
  const { t } = useTranslation();

  const createNativeGroupName = (groupName: string) => {
    const nativeName = t(
      `components.manage_benefits.new.benefit_group_names.${groupName.toLowerCase()}`
    );

    return `${nativeName}Group`;
  };

  const form = useForm<BenefitSettingsForm>({
    mode: 'onChange',
    defaultValues: {
      groups: selectedBenefits?.map(b => {
        return {
          benefitType: b.type,
          maxAmountPerPeriod: benefitMaxAmounts[b.type][0],
          isMaximumSpendingLimit: LimitType.maximum,
          groupId: b.id,
          groupName: createNativeGroupName(b.type),
          fundsLoadingType: undefined,
          recurringTopupRequestEnabled: false
        };
      })
    }
  });

  const onSubmit = (values: BenefitSettingsForm) => {
    const payload: ICreateBenefitGroupsBatchRequest = {
      groups: values.groups.map(group => {
        const isLunch = group.benefitType === LUNCH;
        const isMaximum =
          group.isMaximumSpendingLimit === LimitType.maximum && isLunch;
        return {
          groupId: group.groupId,
          groupName: group.groupName,
          benefitType: group.benefitType,
          maxAmountPerPeriod: Number(
            group.maxAmountPerPeriod.toString().replace(',', '.')
          ),
          recurringTopupRequestEnabled:
            (isLunch &&
              group.fundsLoadingType === FundsLoadingTypes.TopUpRequest &&
              group.recurringTopupRequestEnabled) ??
            false,
          isMaximumSpendingLimit: isMaximum,
          fundsLoadingType: group.fundsLoadingType ?? undefined,
          canApplyFundsLoadingChangeImmediately: false
        };
      })
    };
    mutationTrigger({ payload });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};
